import { userFacingError } from "../userFacingError";

export const SCOPE_KINDS = [
  "space",
  "connected_app",
  "skill",
  "network",
  "user_group",
  "service_account",
  "oidc_provider",
  "tenant",
] as const;
export type ScopeKind = (typeof SCOPE_KINDS)[number];

// Scopes whose access is shared through the generic patchScopeGrants flow.
export const SHAREABLE_SCOPE_KINDS = [
  "space",
  "connected_app",
  "skill",
  "network",
  "service_account",
] as const;
export type ShareableScopeKind = (typeof SHAREABLE_SCOPE_KINDS)[number];

// A grant is tenant-scoped when its scope_id is its own tenant_id — resource
// scope ids (spaces, connectors, …) never collide with a tenants.id, so the
// self-comparison is unambiguous.
export const isTenantScopeGrant = (grant: {
  scopeId: string;
  tenantId?: string | null;
}): boolean => grant.tenantId != null && grant.scopeId === grant.tenantId;

export const PERMISSIONS = [
  "allow_all", // Tenant admin permissions shortcutting access to everything
  "space:read", // Can view space scope
  "space:write", // Can write to all space related objects
  "space:push_live", // Can push workflow drafts live and change whether a live version is in operation (restore, move, archive, pause, recover)
  "space:manage", // Can write to the space object itself, manage space users and git sync, and permanently delete workflows
  "connected_app:read", // Can view connector scope
  "connected_app:write", // Can edit the connector scope (settings, credentials, deletion)
  "connected_app:share", // Can manage who has access to the connector scope
  "connected_app:use", // Can use the connector scope in a space or chat
  "skill:read", // Can view skill scope
  "skill:write", // Can write to skill scope
  "network:read", // Can view network scope
  "network:write", // Can write to network scope
  "network:use", // Can plumb egress routes through the network
  "user_group:read", // Can view user group scope (membership)
  "user_group:write", // Can manage user group scope
  "service_account:read", // Can view a service account and its credentials/grants
  "service_account:assume", // Can mint a temporary credential that acts as this service account
  "service_account:manage", // Can manage a service account: credentials, grants, deletion
  "oidc_provider:manage", // Can manage a single OIDC provider (issuer/audience/jwks/disabled)
  "tenant:connected_app_create", // Can create new connectors
  "tenant:skill_create", // Can create new skills
  "tenant:network_create", // Can create new networks
  "tenant:service_account_create", // Can create new service accounts
  "tenant:service_accounts_read", // Can view all service accounts in the tenant
  "tenant:connected_apps_read", // Can view all connectors in the tenant
  "tenant:connected_app_share", // When creating a connector, also grants the creator share access on it
  "tenant:skills_read", // Can view all skills in the tenant
  "tenant:space_create", // Can create new spaces
  "tenant:spaces_read", // Can view all spaces in the tenant
  "tenant:spaces_manage", // Can manage every space in the tenant: subsumes per-space space:write/push_live/manage (incl. setting access lists), but NOT read — pair with tenant:spaces_read to also view them
  "tenant:user_group_create", // Can create new user groups
  "tenant:users_manage", // Can manage all users in the tenant, adding, deleting
  "tenant:audit_logs_read", // Can view all audit logs in the tenant
  "tenant:audit_logs_manage", // Can configure audit log destinations
  "tenant:ai_providers_manage", // Can manage all AI providers in the tenant
  "tenant:sso_manage", // Can manage all SSO providers in the tenant
  "tenant:oidc_providers_manage", // Can register OIDC identity providers for workload federation
  "tenant:feature_flags_manage", // Can manage all feature flags in the tenant
  "tenant:personal_chats_read", // Can view all personal chats in the tenant
  "tenant:roles_manage", // Can view and manage roles
  "tenant:networks_manage", // Can view and manage networks
  "tenant:egress_routes_manage", // Can add, edit, and remove egress routes in the tenant routing table
  "tenant:egress_rules_manage", // Can add, edit, and remove egress access-control rules in the tenant
] as const;
export type Permission = (typeof PERMISSIONS)[number];

// Accepted from stale cached clients and dropped on write.
export const RETIRED_PERMISSIONS = [
  "tenant:users_read",
  "tenant:roles_read",
] as const;

// The tenant-wide permission that subsumes a given scoped read: a principal
// holding it reads every resource of that kind, not just individually granted
// ones. This is the single source of truth both authorization engines consult —
// the ZQL `canRead*` helpers (services/shared/authorization.ts) and the Drizzle
// `accessExists` (services/api/authorization.ts) — so the REST/MCP path and the
// Zero path can't drift apart.
export const TENANT_READ_FALLBACK: Partial<Record<Permission, Permission>> = {
  "space:read": "tenant:spaces_read",
  "connected_app:read": "tenant:connected_apps_read",
  "skill:read": "tenant:skills_read",
  "network:read": "tenant:networks_manage",
  "service_account:read": "tenant:service_accounts_read",
};

// Same idea for writes: the tenant-wide manage permission subsumes the scoped
// write. Consulted only by `hasAccess` — the read helpers never look up write
// permissions.
export const TENANT_WRITE_FALLBACK: Partial<Record<Permission, Permission>> = {
  "network:write": "tenant:networks_manage",
  "oidc_provider:manage": "tenant:oidc_providers_manage",
  // tenant:spaces_manage subsumes every space-scoped write on every space, so a
  // holder can edit and administer any space (and, via space:manage, set its
  // access list) without a per-space grant. space:read is covered by
  // tenant:spaces_read in TENANT_READ_FALLBACK; the space_manager_all builtin
  // role carries both so all four space permissions resolve.
  "space:write": "tenant:spaces_manage",
  "space:push_live": "tenant:spaces_manage",
  "space:manage": "tenant:spaces_manage",
};

const SCOPE_PREFIX: Record<string, ScopeKind> = {
  space: "space",
  connected_app: "connected_app",
  skill: "skill",
  network: "network",
  user_group: "user_group",
  service_account: "service_account",
  oidc_provider: "oidc_provider",
  tenant: "tenant",
};

// Permissions that exist but may never be put on a custom role. allow_all is
// the tenant-admin shortcut; tenant:spaces_manage is service-account-only until
// the space UI honors TENANT_WRITE_FALLBACK (AccessGraph.hasPermission and its drop
// affordances still check only direct grants), so a human holding it via a
// custom role would be authorized server-side yet see the controls hidden.
const NON_CUSTOM_ROLE_PERMISSIONS: ReadonlySet<Permission> = new Set([
  "allow_all",
  "tenant:spaces_manage",
]);

export function scopeKindOf(permission: Permission): ScopeKind | undefined {
  return SCOPE_PREFIX[permission.slice(0, permission.indexOf(":"))];
}

export function permissionsForScope(scopeKind: ScopeKind): Permission[] {
  return PERMISSIONS.filter(
    (p) => !NON_CUSTOM_ROLE_PERMISSIONS.has(p) && scopeKindOf(p) === scopeKind
  );
}

const REQUIRED_READ_PERMISSIONS: Partial<Record<ScopeKind, Permission>> = {
  space: "space:read",
  connected_app: "connected_app:read",
  skill: "skill:read",
  network: "network:read",
  user_group: "user_group:read",
  service_account: "service_account:read",
};

export function requiredReadPermission(
  scopeKind: ScopeKind
): Permission | undefined {
  return REQUIRED_READ_PERMISSIONS[scopeKind];
}

export function assertCustomRolePermissions(
  scopeKind: ScopeKind,
  permissions: readonly string[]
): void {
  const valid = new Set<string>(permissionsForScope(scopeKind));
  for (const p of permissions) {
    if (NON_CUSTOM_ROLE_PERMISSIONS.has(p as Permission)) {
      throw userFacingError(`${p} cannot be assigned to custom roles`);
    }
    if (!valid.has(p)) {
      throw userFacingError(
        `Permission "${p}" is not valid for scope "${scopeKind}"`
      );
    }
  }
  const required = REQUIRED_READ_PERMISSIONS[scopeKind];
  if (required && !permissions.includes(required)) {
    throw userFacingError(
      `Custom roles for scope "${scopeKind}" must include "${required}"`
    );
  }
}
